import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { LocalizationService } from '../../services/localization.service';
import { AuthService } from '../../services/auth.service';
import { PgUserStatusService, WSServiceUserStatusValue } from '../../pg-ui-molecules/pg-user-status.service';

@Component({
    selector: 'app-pg-user-status',
    templateUrl: './pg-user-status.component.html',
    styleUrls: ['./pg-user-status.component.scss'],
    standalone: false
})
export class PgUserStatusComponent implements OnChanges, OnDestroy {

    constructor(
        private pgUserStatusService:PgUserStatusService,
        private localizationService:LocalizationService,
        private authService:AuthService
    ) {}

    @Input() userId:string;

    /** When true, show display name next to the status dot. */
    @Input() showName = true;

    /**
     * Optional status override (e.g. guests not on user-status).
     * When set, skips watch and uses this value for the dot.
     */
    @Input() statusOverride:WSServiceUserStatusValue = null;

    private _watchedId:string = null;
    private _subscription:Subscription = null;

    displayName = '';
    statusClass = 'secondary';

    ngOnChanges(changes:SimpleChanges): void {
        if(changes['userId'] || changes['statusOverride'] || changes['showName']) {
            this._syncWatch();
            this._refresh();
        }
    }

    ngOnDestroy(): void {
        this._clearWatch();
        if(this._subscription != null) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
    }

    private _isGuest() {
        return this.userId != null && /^guest_/.test(String(this.userId));
    }

    private _syncWatch() {
        let _id = this.userId != null ? String(this.userId) : null;

        if(this.statusOverride != null || this._isGuest() || _id == null) {
            this._clearWatch();
            return;
        }

        if(this._watchedId == _id) return;

        this._clearWatch();
        this._watchedId = _id;
        this.pgUserStatusService.watch(_id);

        if(this._subscription == null) {
            this._subscription = this.pgUserStatusService.usersStatusIndexChange.subscribe(() => {
                this._refresh();
            })
        }
    }

    private _clearWatch() {
        if(this._watchedId != null) {
            this.pgUserStatusService.unwatch(this._watchedId);
            this._watchedId = null;
        }
    }

    private _refresh() {
        if(this.userId == null) {
            this.displayName = '';
            this.statusClass = 'secondary';
            return;
        }

        if(this.statusOverride != null) {
            this.statusClass = this._classFor(this.statusOverride);
        }
        else if(this._isGuest()) {
            this.statusClass = 'success';
        }
        else {
            this.statusClass = this.pgUserStatusService.getStatusClass(this.userId);
        }

        if(!this.showName) {
            this.displayName = '';
            return;
        }

        if(this.authService.isLoggedIn() && String(this.authService.user.id) == String(this.userId)) {
            this.displayName = this.localizationService.translate('pg-customer-care.you');
            return;
        }

        if(this._isGuest()) {
            this.displayName = String(this.userId).replace(/^guest_/, '');
            return;
        }

        let _name = this.pgUserStatusService.getDisplayName(this.userId);
        this.displayName = _name != null ? _name : '...';
    }

    private _classFor(status:WSServiceUserStatusValue) {
        if(status == 'online') return 'success';
        if(status == 'busy') return 'danger';
        return 'secondary';
    }
}
